// Log level configuration
// Levels: 'trace' (-1), 'debug' (0), 'info' (1), 'warning' (2), 'error' (3), 'success' (1)
type LogLevel = "trace" | "debug" | "info" | "warning" | "error" | "success";

interface LoggerConfig {
    currentLogLevel: LogLevel;
    prefix: string;
    enabled: boolean;
}

// Log level hierarchy for filtering
const logLevels: Record<LogLevel, number> = {
    trace: -1,
    debug: 0,
    info: 1,
    success: 1,
    warning: 2,
    error: 3,
};

// Default configuration - can be overridden in development
const defaultConfig: LoggerConfig = {
    currentLogLevel: "debug", // Change this to 'warning' or 'error' to reduce logging
    prefix: "[App]",
    enabled: true,
};

class Logger {
    private config: LoggerConfig;

    constructor(config: Partial<LoggerConfig> = {}) {
        this.config = { ...defaultConfig, ...config };
    }

    private shouldLog(messageLevel: LogLevel): boolean {
        if (!this.config.enabled) return false;

        const currentLevelValue = logLevels[this.config.currentLogLevel];
        const messageLevelValue = logLevels[messageLevel];

        return messageLevelValue >= currentLevelValue;
    }

    private formatMessage(
        level: LogLevel,
        message: string,
        data?: unknown,
    ): string {
        const timestamp = new Date().toISOString().split("T")[1].split(".")[0];
        let formattedMessage = `${this.config.prefix} ${timestamp} [${level.toUpperCase()}] ${message}`;

        if (data !== undefined) {
            formattedMessage += ` ${typeof data === "object" ? JSON.stringify(data, null, 2) : data}`;
        }

        return formattedMessage;
    }

    trace(message: string, ...optionalParams: unknown[]): void {
        if (this.shouldLog("trace")) {
            console.debug(
                this.formatMessage("trace", message),
                ...optionalParams,
            );
        }
    }

    debug(message: string, ...optionalParams: unknown[]): void {
        if (this.shouldLog("debug")) {
            console.log(
                this.formatMessage("debug", message),
                ...optionalParams,
            );
        }
    }

    info(message: string, ...optionalParams: unknown[]): void {
        if (this.shouldLog("info")) {
            console.info(
                this.formatMessage("info", message),
                ...optionalParams,
            );
        }
    }

    success(message: string, ...optionalParams: unknown[]): void {
        if (this.shouldLog("success")) {
            console.log(
                this.formatMessage("success", message),
                ...optionalParams,
            );
        }
    }

    warning(message: string, ...optionalParams: unknown[]): void {
        if (this.shouldLog("warning")) {
            console.warn(
                this.formatMessage("warning", message),
                ...optionalParams,
            );
        }
    }

    error(message: string, ...optionalParams: unknown[]): void {
        if (this.shouldLog("error")) {
            console.error(
                this.formatMessage("error", message),
                ...optionalParams,
            );
        }
    }

    // Configuration methods
    setLogLevel(level: LogLevel): void {
        this.config.currentLogLevel = level;
    }

    setEnabled(enabled: boolean): void {
        this.config.enabled = enabled;
    }

    setPrefix(prefix: string): void {
        this.config.prefix = prefix;
    }
}

// Create the default logger instance
const logger = new Logger();

// Export the default logger
export { logger };

// Export the Logger class for custom instances
export { Logger };
export type { LoggerConfig, LogLevel };

// Convenience function to create a logger with custom prefix
export const createLogger = (prefix: string, config?: Partial<LoggerConfig>) =>
    new Logger({ ...config, prefix: `[${prefix}]` });
